import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BadgeVariant } from '../common/Badge';
import { Badge } from '../common/Badge';
import { EmptyState } from '../common/EmptyState';
import { SkeletonDetail } from '../common/SkeletonDetail';
import { apiClient } from '../../services/api';
import type { VersionChange, VersionEntry, VersionHistoryResponse } from '../../types';
import { formatRelativeTime, formatTimestamp } from '../../utils/dateFormat';

const CATEGORY_ORDER: VersionChange['category'][] = [
  'feature',
  'fix',
  'improvement',
  'breaking',
  'security',
  'documentation',
  'test',
  'removed',
  'note',
];

const CATEGORY_META: Record<VersionChange['category'], { label: string; variant: BadgeVariant }> = {
  feature: { label: 'Feature', variant: 'info' },
  fix: { label: 'Fix', variant: 'error' },
  improvement: { label: 'Improvement', variant: 'success' },
  breaking: { label: 'Breaking', variant: 'warning' },
  security: { label: 'Security', variant: 'purple' },
  documentation: { label: 'Documentation', variant: 'info' },
  test: { label: 'Tests', variant: 'success' },
  removed: { label: 'Removed', variant: 'warning' },
  note: { label: 'Note', variant: 'info' },
};

function groupChanges(entry: VersionEntry): Record<VersionChange['category'], string[]> {
  const grouped: Record<VersionChange['category'], string[]> = {
    feature: [],
    fix: [],
    improvement: [],
    breaking: [],
    security: [],
    documentation: [],
    test: [],
    removed: [],
    note: [],
  };

  entry.changes.forEach((change) => {
    const bucket = Object.hasOwn(grouped, change.category) ? grouped[change.category] : grouped.note;
    bucket.push(change.description);
  });

  return grouped;
}

function VersionTimelineItem({ entry }: { entry: VersionEntry }) {
  const groupedChanges = useMemo(() => groupChanges(entry), [entry]);

  return (
    <li className="relative pl-8">
      <span
        className="absolute left-[0.39rem] top-3 h-3 w-3 rounded-full border-2 border-primary-700 bg-white dark:border-primary-400 dark:bg-gray-900"
        aria-hidden="true"
      />

      <article className="card">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <code className="rounded bg-gray-100 px-2 py-1 text-sm font-semibold text-gray-900 dark:bg-gray-800 dark:text-gray-100">
            {entry.version}
          </code>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {formatTimestamp(entry.releasedAt)} ({formatRelativeTime(entry.releasedAt)})
          </span>
        </div>

        <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">{entry.summary}</p>

        <div className="space-y-3">
          {CATEGORY_ORDER.map((category) => {
            const descriptions = groupedChanges[category];
            if (descriptions.length === 0) return null;
            const metadata = CATEGORY_META[category];

            return (
              <section key={category}>
                <div className="mb-2">
                  <Badge size="sm" variant={metadata.variant}>
                    {metadata.label}
                  </Badge>
                </div>
                <ul className="list-disc space-y-1 pl-5 text-sm text-gray-800 dark:text-gray-200">
                  {descriptions.map((description, index) => (
                    <li key={`${category}-${index}`}>{description}</li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </article>
    </li>
  );
}

export function VersionHistoryTab() {
  const versionHistoryQuery = useQuery<VersionHistoryResponse, Error>({
    queryKey: ['versionHistory'],
    queryFn: async () => apiClient.getVersionHistory(),
    staleTime: 60_000,
  });

  if (versionHistoryQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="card">
          <SkeletonDetail />
        </div>
        <div className="card">
          <SkeletonDetail />
        </div>
      </div>
    );
  }

  if (versionHistoryQuery.isError) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">Unable to load version history</h2>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          {versionHistoryQuery.error?.message || 'An unexpected error occurred while loading version history.'}
        </p>
        <button
          type="button"
          onClick={() => void versionHistoryQuery.refetch()}
          className="mt-4 rounded border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
        >
          Try again
        </button>
      </div>
    );
  }

  const versions = versionHistoryQuery.data?.versions ?? [];
  if (versions.length === 0) {
    return (
      <EmptyState
        title="No version history available"
        message="No release entries were returned by the API."
      />
    );
  }

  return (
    <section className="relative pl-2">
      <div className="absolute bottom-0 left-[0.75rem] top-0 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
      <ol className="space-y-6">
        {versions.map((entry) => (
          <VersionTimelineItem key={`${entry.version}-${entry.releasedAt}`} entry={entry} />
        ))}
      </ol>
    </section>
  );
}
